import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { DataTableColumn } from "@/components/ui/DataTable/types";
import { ActiveOrderSplitRow } from "./types";

export const DEFAULT_COLUMN_VISIBILITY = {
  actions: true,
  exchange_event_time: true,
  trading_account_name: true,
  user_name: true,
  instrument_name: true,
  buy_sell_type: true,
  order_quantity: true,
  working_quantity: true,
  filled_quantity: true,
  remaining_quantity: true,
  limit_price: true,
  customer_name: true,
  split_percentage: true,
  text_a: false,
  text_b: false,
  text_c: false,
  text_tt: false,
  order_uom_quantity: false,
};

/**
 * Aggregate-cell helper: read the field straight from `parent` on any leaf
 * under this aggregate group. No math, no rollups — leaves are guaranteed to
 * share the same parent (they are split-rows of one order).
 */
function aggParent<K extends keyof ActiveOrderSplitRow["parent"]>(key: K) {
  return (info: any) => {
    const leaves = info.row.getLeafRows() as Array<{ original: ActiveOrderSplitRow }>;
    const first = leaves[0]?.original;
    if (!first) return "-";
    const value = first.parent[key];
    return value == null || value === "" ? "-" : String(value);
  };
}

function aggParentDate(key: keyof ActiveOrderSplitRow["parent"]) {
  return (info: any) => {
    const leaves = info.row.getLeafRows() as Array<{ original: ActiveOrderSplitRow }>;
    const first = leaves[0]?.original;
    if (!first) return "-";
    const value = first.parent[key];
    if (!value) return "-";
    return moment.tz(value as string, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a");
  };
}

/**
 * Builds the column list for the TT v2 active-orders table. The `actions`
 * column is wired by the table component (it supplies the cell renderer) so
 * this file stays free of JSX.
 */
export function buildBaseColumns(): DataTableColumn<ActiveOrderSplitRow>[] {
  return [
    {
      id: "exchange_event_time",
      accessorKey: "exchange_event_time",
      header: "First Event",
      size: 140,
      minSize: 120,
      columnFilterType: "date",
      filterDateTimezone: CommonDestinationTimezone,
      filterDisplayFn: (v) =>
        moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD hh:mm a"),
      filterParseDateFn: (v) => moment.tz(v as string, CommonDestinationTimezone),
      cell: (info) =>
        moment
          .tz(info.getValue() as string, CommonDestinationTimezone)
          .format("MM/DD/YYYY hh:mm a"),
      aggregatedCell: aggParentDate("exchange_event_time"),
    },
    {
      id: "trading_account_name",
      accessorKey: "trading_account_name",
      header: "Account",
      size: 120,
      minSize: 100,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("trading_account_name"),
    },
    {
      id: "user_name",
      accessorKey: "user_name",
      header: "User",
      size: 140,
      minSize: 100,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("user_name"),
    },
    {
      id: "instrument_name",
      accessorKey: "instrument_name",
      header: "Instrument",
      size: 220,
      minSize: 150,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("instrument_name"),
    },
    {
      id: "buy_sell_type",
      accessorKey: "buy_sell_type",
      header: "B/S",
      size: 70,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("buy_sell_type"),
    },
    {
      id: "order_quantity",
      accessorKey: "order_quantity",
      header: "Order",
      size: 80,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("order_quantity"),
    },
    {
      id: "working_quantity",
      accessorKey: "working_quantity",
      header: "Working",
      size: 80,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("working_quantity"),
    },
    {
      id: "filled_quantity",
      accessorKey: "filled_quantity",
      header: "Filled",
      size: 80,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("filled_quantity"),
    },
    {
      id: "remaining_quantity",
      accessorKey: "remaining_quantity",
      header: "Remaining",
      size: 90,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("remaining_quantity"),
    },
    {
      id: "limit_price",
      accessorKey: "limit_price",
      header: "Limit Price",
      size: 100,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("limit_price"),
    },
    {
      id: "customer_name",
      accessorKey: "customer_name",
      header: "Customer",
      size: 220,
      minSize: 140,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      // Aggregate row hides per-split customer name (leaves expose them).
      aggregatedCell: () => "",
    },
    {
      id: "split_percentage",
      accessorKey: "split_percentage",
      header: "Split %",
      size: 80,
      minSize: 60,
      cell: (info) => {
        const v = info.getValue() as number | string | null;
        if (v == null) return "-";
        return `${v}%`;
      },
      aggregatedCell: () => "",
    },
    {
      id: "text_a",
      accessorKey: "text_a",
      header: "Text A",
      size: 120,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("text_a"),
    },
    {
      id: "text_b",
      accessorKey: "text_b",
      header: "Text B",
      size: 120,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("text_b"),
    },
    {
      id: "text_c",
      accessorKey: "text_c",
      header: "Text C",
      size: 120,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("text_c"),
    },
    {
      id: "text_tt",
      accessorKey: "text_tt",
      header: "Text TT",
      size: 120,
      minSize: 80,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("text_tt"),
    },
    {
      id: "order_uom_quantity",
      accessorKey: "order_uom_quantity",
      header: "Order UOM",
      size: 90,
      minSize: 60,
      cell: (info) => (info.getValue() as string | null) ?? "-",
      aggregatedCell: aggParent("order_uom_quantity"),
    },
    // Grouping key column. tanstack hoists the grouped column to the front;
    // the DataTable renders `cell` for both the grouped (aggregate header) row
    // AND each leaf, then auto-appends " (N)" to the grouped row. We blank
    // both renderers so this column is just the chevron + leaf count and
    // doesn’t take any space showing the GUID itself. Grouping is driven by
    // accessorKey, not by the cell renderer, so blanking is safe.
    {
      id: "guid_order_id",
      accessorKey: "guid_order_id",
      header: "",
      size: 60,
      minSize: 50,
      cell: () => "",
      aggregatedCell: () => "",
    },
  ];
}
